import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";

export interface Column<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  cell?: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  sortable?: boolean;
  width?: string;
}

export interface RowAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: "default" | "destructive";
}

interface AirtableGridProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  getRowActions?: (row: T) => RowAction[];
  selectedRows?: Set<string | number>;
  onSelectRow?: (id: string | number, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  getRowId: (row: T) => string | number;
  emptyState?: ReactNode;
  loading?: boolean;
  /** "compact": single-line 13px rows for the entity list pages. Default leaves every other screen unchanged. */
  density?: "comfortable" | "compact";
  /** Fill the parent's height and scroll inside, so the sticky header works within a panel. */
  scroll?: boolean;
}

export function AirtableGrid<T>({
  data,
  columns,
  onRowClick,
  getRowActions,
  selectedRows,
  onSelectRow,
  onSelectAll,
  getRowId,
  emptyState,
  loading,
  density = "comfortable",
  scroll = false,
}: AirtableGridProps<T>) {
  const compact = density === "compact";
  const outerCls = scroll ? "h-full min-h-0 flex flex-col bg-background" : "bg-background rounded-lg border border-border overflow-hidden shadow-sm";
  const innerCls = scroll ? "flex-1 min-h-0 overflow-auto" : "overflow-x-auto";
  const thCls = compact
    ? "px-2 py-1.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap overflow-hidden text-ellipsis"
    : "px-4 py-3 text-left text-[11px] font-bold text-muted-foreground/80 uppercase tracking-wider whitespace-nowrap";
  // align-top on inline-flex and inline-block children and inline svg icons: baseline-aligned in the tight line box, a badge or a block-svg check (its baseline is its bottom edge) would stretch every compact row.
  const tdCls = compact
    ? "px-2 py-1 text-[13px] leading-tight [&_.inline-flex]:align-top [&_.inline-block]:align-top [&_svg]:align-top whitespace-nowrap overflow-hidden text-ellipsis"
    : "px-4 py-3 text-sm";
  const selectCellCls = compact ? "w-9 px-2 py-0 leading-none" : "w-12 px-3 py-3";
  const actionCellCls = compact ? "w-9 px-1 py-0 leading-none" : "w-12 px-2 py-3";
  const hasSelection = selectedRows !== undefined && onSelectRow !== undefined;
  const allSelected = data.length > 0 && data.every(row => selectedRows?.has(getRowId(row)));
  const someSelected = !allSelected && data.some(row => selectedRows?.has(getRowId(row)));

  if (loading) {
    return (
      <div className={scroll ? outerCls : "bg-background rounded-lg border border-border overflow-hidden"}>
        <div className="animate-pulse">
          <div className={cn(compact ? "h-8" : "h-11", "bg-muted/50 border-b border-border")} />
          {[...Array(5)].map((_, i) => (
            <div key={i} className={cn(compact ? "h-8" : "h-14", "border-b border-border/50 flex items-center gap-4 px-4")}>
              <div className="h-4 w-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded flex-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0 && emptyState) {
    return (
      <div className={scroll ? outerCls : "bg-background rounded-lg border border-border overflow-hidden"}>
        <div className="sticky top-0 z-10 bg-muted/30 border-b border-border">
          <div className="flex">
            {hasSelection && (
              <div className={cn(selectCellCls, "flex items-center justify-center border-r border-border/50")} />
            )}
            {columns.map((column) => (
              <div
                key={column.id}
                className={cn(
                  compact ? "px-2 py-1.5" : "px-4 py-3",
                  "text-xs font-semibold text-muted-foreground uppercase tracking-wider",
                  column.width || "flex-1",
                  column.headerClassName
                )}
              >
                {column.header}
              </div>
            ))}
          </div>
        </div>
        <div className={scroll ? "py-16 flex-1 min-h-0 overflow-auto" : "py-16"}>{emptyState}</div>
      </div>
    );
  }

  return (
    <div className={outerCls}>
      <div className={innerCls}>
        <table className="w-full table-fixed">
          <thead className="sticky top-0 z-10">
            <tr className={compact ? "bg-card border-b border-border" : "bg-muted/40 border-b-2 border-border"}>
              {hasSelection && (
                <th className={cn(selectCellCls, "border-r border-border/30")}>
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={(checked) => onSelectAll?.(checked === true)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    aria-label="Select all rows on this page"
                    data-testid="grid-select-all"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={cn(
                    thCls,
                    "border-r border-border/20 last:border-r-0",
                    column.sortable && "cursor-pointer hover:text-foreground hover:bg-muted/50 transition-colors",
                    column.width,
                    column.headerClassName
                  )}
                  data-testid={`column-header-${column.id}`}
                >
                  {column.header}
                </th>
              ))}
              {getRowActions && <th className={actionCellCls} />}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {data.map((row, index) => {
              const rowId = getRowId(row);
              const isSelected = selectedRows?.has(rowId);
              const actions = getRowActions?.(row);

              return (
                <tr
                  key={rowId}
                  onClick={() => onRowClick?.(row)}
                  className={cn(
                    "group transition-all duration-100",
                    onRowClick && "cursor-pointer",
                    isSelected
                      ? "bg-primary/5"
                      : compact || index % 2 === 0
                      ? "bg-background"
                      : "bg-muted/20",
                    "hover:bg-primary/[0.03]"
                  )}
                  style={{ animationDelay: `${index * 20}ms` }}
                  data-testid={`grid-row-${rowId}`}
                >
                  {hasSelection && onSelectRow && (
                    <td
                      className={cn(selectCellCls, "border-r border-border/20")}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => onSelectRow(rowId, checked === true)}
                        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        aria-label={`Select ${(row as any).name || rowId}`}
                        data-testid={`grid-row-${rowId}-checkbox`}
                      />
                    </td>
                  )}
                  {columns.map((column, columnIndex) => (
                    <td
                      key={column.id}
                      className={cn(tdCls, !compact && "border-r border-border/10 last:border-r-0", column.className)}
                      title={compact && !column.cell && column.accessorKey ? String(row[column.accessorKey] ?? "") : undefined}
                    >
                      {columnIndex === 0 && onRowClick ? (
                        <button type="button" className={compact ? "text-left block w-full truncate rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" : "text-left w-full rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"} aria-label={`Open ${(row as any).name || rowId}`} onClick={(event) => { event.stopPropagation(); onRowClick(row); }}>
                          {column.cell ? column.cell(row) : column.accessorKey ? String(row[column.accessorKey] ?? "") : "Open record"}
                        </button>
                      ) : column.cell ? column.cell(row) : column.accessorKey ? String(row[column.accessorKey] ?? "") : ""}
                    </td>
                  ))}
                  {getRowActions && (
                    <td className={actionCellCls} onClick={(e) => e.stopPropagation()}>
                      <div className={compact ? "opacity-100 flex items-center" : "opacity-100"}>
                        {actions && actions.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={compact ? "h-5 w-5 min-h-0 p-0" : "h-7 w-7 p-0"}
                                aria-label={`Actions for ${(row as any).name || rowId}`}
                                data-testid={`row-actions-${rowId}`}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {actions.map((action, i) => (
                                <DropdownMenuItem
                                  key={i}
                                  onClick={action.onClick}
                                  className={cn(
                                    "gap-2",
                                    action.variant === "destructive" && "text-destructive"
                                  )}
                                >
                                  {action.icon}
                                  {action.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface GridFooterProps {
  showing: number;
  total: number;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

export function GridFooter({
  showing,
  total,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
}: GridFooterProps) {
  return (
    <div className="flex items-center justify-between px-1 py-3 text-sm text-muted-foreground">
      <p>
        Showing <span className="font-medium text-foreground">{showing}</span> of{" "}
        <span className="font-medium text-foreground">{total}</span> records
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!hasPrevious}
          onClick={onPrevious}
          className="h-8 px-3 text-xs"
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!hasNext}
          onClick={onNext}
          className="h-8 px-3 text-xs"
        >
          Next
        </Button>
      </div>
    </div>
  );
}
